import useIsSmall from "@/hooks/useIsSmall";
import { ReportKind, finance, financeKeys } from "@/services/finance";
import { Alert, Badge, Button, Group, Modal, SegmentedControl, Select, Skeleton, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import classes from "@/modules/finance/finance.module.css";
import { DateField, MoneyInput, faDate, faNum, isoToday, rial, toNumber, useFinance, useFinanceWrite, useTerminals } from "@/modules/finance/shared";

const KINDS: { value: ReportKind | "close"; label: string; note: string }[] = [
  { value: "cash-flow", label: "دریافت و پرداخت", note: "گردش صندوق و بانک در بازه؛ این عدد سود نیست." },
  { value: "sales", label: "فروش", note: "فروش در بازه، بر اساس تاریخ فاکتور." },
  { value: "receivables", label: "مطالبات", note: "مانده دریافت‌نشده تا پایان بازه." },
  { value: "close", label: "کنترل صندوق و پوز", note: "" },
];

export default function Reports() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as ReportKind | "close") || "cash-flow";
  return (
    <>
      <SegmentedControl value={tab} onChange={(t) => setParams({ tab: t }, { replace: true })} data={KINDS.map((k) => ({ value: k.value, label: k.label }))} />
      {tab === "close" ? <DayControl /> : <Report kind={tab} />}
    </>
  );
}

function Report({ kind }: { kind: ReportKind }) {
  const { range, can } = useFinance();
  const meta = KINDS.find((k) => k.value === kind)!;
  const query = useInfiniteQuery({
    queryKey: [...financeKeys.all, "report", kind, range],
    queryFn: ({ pageParam }) => finance.report(kind, { from: range.from, to: range.to, limit: 50, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor || undefined,
  });
  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];
  const first = query.data?.pages[0];
  const exporter = useExport();

  return (
    <section className={classes.panel}>
      <div className={classes.panelHead}>
        <div><h2>{meta.label}</h2><small>{meta.note}</small></div>
        {can("finance.export") && kind !== "receivables" && <Button variant="default" leftSection={<IconDownload size={16} />} loading={exporter.busy} onClick={() => exporter.start(kind === "cash-flow" ? "cash-flow" : "sales", range.from, range.to)}>خروجی CSV</Button>}
      </div>
      {first && <Text size="sm">جمع کل بازه: <b>{rial(first.totals?.amountRial)}</b> · {faNum(first.totalCount)} ردیف</Text>}
      {query.isLoading ? <Skeleton height={200} /> : query.isError ? <Alert color="red">دریافت گزارش انجام نشد.<Button variant="subtle" onClick={() => query.refetch()}>تلاش دوباره</Button></Alert> : !rows.length ? <div className={classes.empty}>در این بازه موردی ثبت نشده است.</div> : (
        <>
          <div className={classes.tableWrap}>
            <table>
              <thead><tr><th>تاریخ</th><th>شرح</th><th>منبع</th><th className={classes.num}>مبلغ</th></tr></thead>
              <tbody>{rows.map((r) => <tr key={r.id}><td>{faDate(r.businessDate)}</td><td>{r.memo || "—"}</td><td>{r.source}</td><td className={classes.num}>{rial(r.amountRial)}</td></tr>)}</tbody>
            </table>
          </div>
          {query.hasNextPage && <Button variant="default" loading={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>نمایش بیشتر</Button>}
        </>
      )}
    </section>
  );
}

// The server builds the CSV for the whole filter; it is fetched by id until it is ready.
function useExport() {
  const [busy, setBusy] = useState(false);
  const create = useFinanceWrite((body: { kind: string; from: string; to: string }, key) => finance.createExport(body, key));
  const start = async (kind: string, from: string, to: string) => {
    setBusy(true);
    try {
      const job = await create.run({ kind, from, to });
      if (!job) return;
      for (let i = 0; i < 20; i++) {
        const result = await finance.exportJob(job.id);
        if (result.status === "Ready" && result.content != null) {
          const blob = new Blob([result.content], { type: "text/csv;charset=utf-8" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = result.fileName || `finance-${kind}.csv`;
          link.click();
          URL.revokeObjectURL(link.href);
          return;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
    } finally {
      setBusy(false);
    }
  };
  return { start, busy };
}

function DayControl() {
  const { range, can } = useFinance();
  const day = range.to;
  const preview = useQuery({ queryKey: [...financeKeys.all, "daily", day], queryFn: () => finance.dailyPreview(day), enabled: can("finance.close") });
  const clearing = useQuery({ queryKey: [...financeKeys.all, "clearing"], queryFn: finance.clearing, enabled: can("finance.settle") });
  const terminals = useTerminals(can("finance.settle"));
  const [actual, setActual] = useState("");
  const [reason, setReason] = useState("");
  const [settling, setSettling] = useState(false);
  const close = useFinanceWrite((body: { businessDate: string; actualCashRial: string; reason?: string }, key) => finance.closeDay(body, key), { success: "روز بسته شد" });

  const expected = toNumber(preview.data?.expectedCashRial);
  const diff = actual === "" ? null : Number(actual) - expected;

  return (
    <div className={classes.cols}>
      {can("finance.close") && (
        <section className={classes.panel}>
          <div className={classes.panelHead}>
            <div><h2>کنترل پایان روز {faDate(day)}</h2><small>روز را از فیلتر «تا» عوض کنید. صندوق را بشمارید و عدد واقعی را وارد کنید.</small></div>
            {preview.data && <Badge variant="light" color={preview.data.closed ? "green" : "orange"}>{preview.data.closed ? "بسته شده" : "باز"}</Badge>}
          </div>
          {preview.isLoading ? <Skeleton height={160} /> : preview.data && (
            <>
              {preview.data.needsReview && <Alert color="orange">پس از بستن این روز تراکنش درگاه رسیده است؛ بررسی کنید.</Alert>}
              <div className={classes.list}>
                <div className={classes.row}><div><strong>صندوق نقدی</strong><small>انتظار از روی ثبت‌ها</small></div><span className={classes.amount}>{rial(expected)}</span></div>
                {preview.data.terminals.map((t) => <div key={t.terminalId} className={classes.row}><div><strong>پوز {t.name}</strong><small>جمع تراکنش همین روز؛ با مانده تسویه‌نشده فرق دارد</small></div><span className={classes.amount}>{rial(t.expectedRial)}</span></div>)}
              </div>
              {!preview.data.closed && (
                <Stack gap="sm">
                  <MoneyInput label="موجودی شمرده‌شدهٔ صندوق" value={actual} onChange={setActual} />
                  {diff !== null && <Text size="sm" c={diff === 0 ? "green.8" : "red.7"}>اختلاف: {rial(diff)}</Text>}
                  {diff !== null && diff !== 0 && <Textarea label="دلیل اختلاف" withAsterisk autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} description="اختلاف مانده را خودکار اصلاح نمی‌کند." />}
                  <Group><Button color="violet" disabled={actual === "" || (diff !== 0 && !reason.trim())} loading={close.pending} onClick={() => close.run({ businessDate: day, actualCashRial: actual, reason: reason.trim() || undefined })}>بستن روز</Button></Group>
                  <Text size="xs" c="dimmed">بعد از بستن، ثبت دستی همین روز پذیرفته نمی‌شود.</Text>
                </Stack>
              )}
            </>
          )}
        </section>
      )}
      {can("finance.settle") && (
        <section className={classes.panel}>
          <div className={classes.panelHead}>
            <div><h2>پوز در انتظار واریز</h2><small>وقتی بانک واریز کرد، تسویه را ثبت کنید. کارمزد هزینهٔ جداست.</small></div>
            {!!terminals.data?.length && <Button variant="light" color="violet" onClick={() => setSettling(true)}>ثبت واریز</Button>}
          </div>
          {!terminals.data?.length ? <div className={classes.empty}>دستگاه پوزی ثبت نشده است.</div> : (
            <div className={classes.list}>
              {terminals.data.map((t) => {
                const balance = clearing.data?.find((c) => c.id === t.clearingAccountId)?.balanceRial;
                return <div key={t.id} className={classes.row}><div><strong>{t.name}</strong><small>{t.code}</small></div><span className={classes.amount}>{rial(balance)}</span></div>;
              })}
            </div>
          )}
        </section>
      )}
      {settling && <Settlement onClose={() => setSettling(false)} balances={clearing.data ?? []} />}
    </div>
  );
}

function Settlement({ onClose, balances }: { onClose: () => void; balances: { id: number; balanceRial: string }[] }) {
  const small = useIsSmall();
  const terminals = useTerminals();
  const [v, setV] = useState({ terminalId: "", gross: "", fee: "0", date: isoToday(), reference: "" });
  const terminal = terminals.data?.find((t) => String(t.id) === v.terminalId);
  const pending = toNumber(balances.find((b) => b.id === terminal?.clearingAccountId)?.balanceRial);
  const save = useFinanceWrite((body: object, key) => finance.settle(body, key), { success: "واریز ثبت شد", onSuccess: onClose });
  const gross = Number(v.gross || 0), fee = Number(v.fee || 0);
  const valid = terminal && gross > 0 && fee >= 0 && fee <= gross && gross <= pending && v.reference.trim();
  return (
    <Modal opened onClose={onClose} title="ثبت واریز پوز به بانک" {...(small ? { fullScreen: true, radius: 0 } : { centered: true })}>
      <Stack>
        <Select label="دستگاه" withAsterisk data={(terminals.data ?? []).map((t) => ({ value: String(t.id), label: t.name }))} value={v.terminalId} onChange={(t) => setV({ ...v, terminalId: t ?? "" })} />
        {terminal && <Text size="sm" c="dimmed">در انتظار واریز: {rial(pending)}</Text>}
        <MoneyInput label="مبلغ قبل از کارمزد" withAsterisk value={v.gross} onChange={(gross) => setV({ ...v, gross })} />
        <MoneyInput label="کارمزد کسرشده" value={v.fee} onChange={(fee) => setV({ ...v, fee })} />
        <DateField label="تاریخ واریز" value={v.date} max={isoToday()} onChange={(date) => setV({ ...v, date })} />
        <TextInput label="شماره مرجع واریز" withAsterisk value={v.reference} onChange={(e) => setV({ ...v, reference: e.currentTarget.value })} />
        <Text size="sm">به بانک اضافه می‌شود: <b>{rial(Math.max(0, gross - fee))}</b></Text>
        <Group justify="flex-end"><Button variant="default" onClick={onClose}>انصراف</Button><Button color="violet" disabled={!valid} loading={save.pending} onClick={() => save.run({ terminalId: terminal!.id, grossRial: v.gross, feeRial: v.fee || "0", businessDate: v.date, reference: v.reference.trim() })}>ثبت واریز</Button></Group>
      </Stack>
    </Modal>
  );
}
